/**
 * SeguimientoCliente.jsx
 * 
 * Página de seguimiento para clientes
 * 
 * Qué hace:
 * - Muestra la ubicación del repartidor en tiempo real
 * - Muestra la ubicación del cliente
 * - Muestra la ruta hacia el cliente
 * - Calcula distancia y tiempo estimado de llegada
 * 
 * Qué recibe:
 * - Parámetro de ruta: id (ID único de la entrega)
 * 
 * Qué devuelve:
 * - JSX con la página de seguimiento
 */

import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useSimulacion } from '../context/SimulacionContext'
import MapaSeguimiento from '../components/MapaSeguimiento'
import './SeguimientoCliente.css'

function SeguimientoCliente() {
  const { id } = useParams()
  const {
    posicionRepartidor,
    setPosicionRepartidor,
    ruta,
    setRuta,
    puntosEntrega,
    setPuntosEntrega,
    puntoInicial,
    setPuntoInicial,
    simulando,
    setSimulando
  } = useSimulacion()

  const [puntoCliente, setPuntoCliente] = useState(null)
  const [distanciaRestante, setDistanciaRestante] = useState(0)
  const [tiempoEstimado, setTiempoEstimado] = useState(0)
  const [estado, setEstado] = useState('esperando') // 'esperando', 'en-camino', 'cerca', 'llegando'

  // Datos dummy del cliente
  const datosCliente = {
    nombre: 'Cliente de Prueba',
    telefono: '+54 9 381 123-4567',
    numeroPedido: `#${id?.substring(id.length - 6) || '000000'}`,
    direccion: 'Dirección de entrega'
  }

  // Función para buscar el punto de entrega
  const buscarPuntoEntrega = () => {
    if (!id) {
      setPuntoCliente(null)
      return
    }

    // Primero intentar buscar en los puntos del contexto
    if (puntosEntrega && puntosEntrega.length > 0) {
      const punto = puntosEntrega.find(p => p && p.id === id)
      if (punto) {
        setPuntoCliente(punto)
        datosCliente.direccion = punto.direccion || 'Dirección de entrega'
        return
      }
    }

    // Si no se encuentra en el contexto, intentar cargar desde localStorage
    try {
      const stored = localStorage.getItem('turutatrack_simulacion')
      if (stored) {
        const data = JSON.parse(stored)
        if (data.puntosEntrega && Array.isArray(data.puntosEntrega)) {
          const punto = data.puntosEntrega.find(p => p && p.id === id)
          if (punto) {
            setPuntoCliente(punto)
            datosCliente.direccion = punto.direccion || 'Dirección de entrega'
            // Actualizar el contexto con los datos del storage si están vacíos
            if (data.puntosEntrega.length > 0 && puntosEntrega.length === 0) {
              setPuntosEntrega(data.puntosEntrega)
              setRuta(data.ruta || [])
              setPuntoInicial(data.puntoInicial || null)
              setPosicionRepartidor(data.posicionRepartidor || null)
              setSimulando(data.simulando || false)
              console.log('Datos cargados desde localStorage')
            }
            return
          }
        }
      }
    } catch (error) {
      console.error('Error cargando desde localStorage:', error)
    }

    // Si no se encuentra en ningún lado, intentar cargar desde storage una vez más
    try {
      const stored = localStorage.getItem('turutatrack_simulacion')
      if (stored) {
        const data = JSON.parse(stored)
        console.log('Datos en localStorage:', {
          puntosCount: data.puntosEntrega?.length || 0,
          ids: data.idsEntrega,
          puntosIds: data.puntosEntrega?.map(p => p?.id).filter(Boolean) || []
        })
      }
    } catch (error) {
      console.error('Error leyendo storage:', error)
    }
    
    console.log('Punto no encontrado')
    console.log('ID buscado:', id)
    console.log('Puntos en contexto:', puntosEntrega?.length || 0)
    if (puntosEntrega && puntosEntrega.length > 0) {
      console.log('IDs disponibles en contexto:', puntosEntrega.map(p => p?.id).filter(Boolean))
    }
    setPuntoCliente(null)
  }

  // Cargar datos desde localStorage al montar el componente
  useEffect(() => {
    // Función para cargar y actualizar
    const cargarYActualizar = () => {
      try {
        const stored = localStorage.getItem('turutatrack_simulacion')
        if (stored) {
          const data = JSON.parse(stored)
          console.log('📦 Cargando datos desde localStorage al montar:', {
            puntosCount: data.puntosEntrega?.length || 0,
            tieneRuta: !!data.ruta,
            tienePuntoInicial: !!data.puntoInicial,
            idsDisponibles: data.puntosEntrega?.map(p => p?.id).filter(Boolean) || [],
            rawData: data // Para debug completo
          })
          
          // Actualizar el contexto con los datos del storage INMEDIATAMENTE
          if (data.puntosEntrega && Array.isArray(data.puntosEntrega) && data.puntosEntrega.length > 0) {
            // Actualizar el estado del contexto
            setPuntosEntrega(data.puntosEntrega)
            console.log('✅ Puntos de entrega cargados en contexto:', data.puntosEntrega.length)
            
            // Buscar el punto inmediatamente después de cargar
            if (id) {
              const punto = data.puntosEntrega.find(p => p && p.id === id)
              if (punto) {
                setPuntoCliente(punto)
                datosCliente.direccion = punto.direccion || 'Dirección de entrega'
                console.log('✅✅✅ PUNTO ENCONTRADO AL CARGAR:', punto.id)
                return true // Encontrado
              } else {
                console.log('⚠️ Punto no encontrado con ID:', id)
                console.log('IDs disponibles:', data.puntosEntrega.map(p => p?.id).filter(Boolean))
                console.log('Comparación detallada:', {
                  buscado: id,
                  tipoBuscado: typeof id,
                  disponibles: data.puntosEntrega.map(p => ({
                    id: p.id,
                    tipoId: typeof p.id,
                    match: p.id === id,
                    matchString: String(p.id) === String(id)
                  }))
                })
              }
            }
          } else {
            console.log('⚠️ No hay puntos de entrega en los datos cargados')
          }
          
          // Actualizar otros campos
          if (data.ruta && Array.isArray(data.ruta)) {
            setRuta(data.ruta)
          }
          if (data.puntoInicial) {
            setPuntoInicial(data.puntoInicial)
          }
          if (data.posicionRepartidor) {
            setPosicionRepartidor(data.posicionRepartidor)
          }
          if (data.simulando !== undefined) {
            setSimulando(data.simulando)
          }
          
          return false // No encontrado aún
        } else {
          // Verificar directamente qué hay en localStorage
          const rawStored = localStorage.getItem('turutatrack_simulacion')
          console.log('⚠️ No hay datos en localStorage', {
            rawStored: rawStored,
            isNull: rawStored === null,
            isUndefined: rawStored === undefined,
            length: rawStored?.length,
            firstChars: rawStored?.substring(0, 100)
          })
          
          // Intentar leer todas las claves de localStorage para debug
          const allKeys = []
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i)
            allKeys.push(key)
          }
          console.log('🔍 Todas las claves en localStorage:', allKeys)
          
          return false
        }
      } catch (error) {
        console.error('❌ Error cargando desde localStorage al montar:', error)
        return false
      }
    }
    
    // Intentar cargar inmediatamente
    const encontrado = cargarYActualizar()
    
    // Si no se encontró, intentar de nuevo después de un breve delay
    // Esto da tiempo a que el contexto se inicialice completamente
    if (!encontrado && id) {
      const timeout1 = setTimeout(() => {
        console.log('🔄 Reintentando cargar datos (intento 1)...')
        cargarYActualizar()
      }, 100)
      
      const timeout2 = setTimeout(() => {
        console.log('🔄 Reintentando cargar datos (intento 2)...')
        cargarYActualizar()
      }, 500)
      
      return () => {
        clearTimeout(timeout1)
        clearTimeout(timeout2)
      }
    }
  }, [id]) // Ejecutar cuando cambie el ID o al montar

  // Buscar el punto cuando cambie el ID o los puntos de entrega
  useEffect(() => {
    buscarPuntoEntrega()
  }, [id, puntosEntrega])

  // Escuchar cambios en localStorage (para sincronizar entre pestañas)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'turutatrack_simulacion') {
        // Recargar datos desde storage
        try {
          const stored = localStorage.getItem('turutatrack_simulacion')
          if (stored) {
            const data = JSON.parse(stored)
            // Actualizar el contexto con los datos del storage
            if (data.puntosEntrega && Array.isArray(data.puntosEntrega)) {
              setPuntosEntrega(data.puntosEntrega)
            }
            if (data.ruta) {
              setRuta(data.ruta)
            }
            if (data.puntoInicial) {
              setPuntoInicial(data.puntoInicial)
            }
            if (data.posicionRepartidor) {
              setPosicionRepartidor(data.posicionRepartidor)
            }
            if (data.simulando !== undefined) {
              setSimulando(data.simulando)
            }
          }
        } catch (error) {
          console.error('Error sincronizando desde storage:', error)
        }
        buscarPuntoEntrega()
      }
    }

    // Escuchar cambios en storage (desde otras pestañas)
    window.addEventListener('storage', handleStorageChange)
    
    // También verificar periódicamente (para sincronizar desde la misma pestaña)
    const interval = setInterval(() => {
      // Primero sincronizar los datos desde storage
      try {
        const stored = localStorage.getItem('turutatrack_simulacion')
        if (stored) {
          const data = JSON.parse(stored)
          
          // Actualizar puntos de entrega si están vacíos o si hay cambios
          if (data.puntosEntrega && Array.isArray(data.puntosEntrega) && data.puntosEntrega.length > 0) {
            if (puntosEntrega.length === 0 || 
                puntosEntrega.length !== data.puntosEntrega.length ||
                puntosEntrega[0]?.id !== data.puntosEntrega[0]?.id) {
              setPuntosEntrega(data.puntosEntrega)
              console.log('🔄 Sincronizando puntos de entrega desde storage:', data.puntosEntrega.length)
            }
          }
          
          // Actualizar ruta si hay cambios
          if (data.ruta && Array.isArray(data.ruta) && data.ruta.length > 0) {
            if (!ruta || ruta.length === 0) {
              setRuta(data.ruta)
            }
          }
          
          // Actualizar punto inicial si hay cambios
          if (data.puntoInicial && !puntoInicial) {
            setPuntoInicial(data.puntoInicial)
          }
          
          // Actualizar posición del repartidor
          if (data.posicionRepartidor) {
            if (!posicionRepartidor || 
                posicionRepartidor.lat !== data.posicionRepartidor.lat || 
                posicionRepartidor.lng !== data.posicionRepartidor.lng) {
              setPosicionRepartidor(data.posicionRepartidor)
            }
          }
          
          // Actualizar estado de simulación
          if (data.simulando !== simulando) {
            setSimulando(data.simulando)
          }
        }
      } catch (error) {
        // Ignorar errores silenciosamente
      }
      
      // Luego buscar el punto
      buscarPuntoEntrega()
    }, 500) // Verificar cada 500ms para mejor sincronización

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      clearInterval(interval)
    }
  }, [id, posicionRepartidor, simulando]) // Dependencias necesarias

  // Calcular distancia y tiempo estimado
  useEffect(() => {
    if (!posicionRepartidor || !puntoCliente) {
      setDistanciaRestante(0)
      setTiempoEstimado(0)
      setEstado('esperando')
      return
    }

    // Calcular distancia en metros usando fórmula de Haversine
    const R = 6371000 // Radio de la Tierra en metros
    const lat1 = posicionRepartidor.lat * Math.PI / 180
    const lat2 = puntoCliente.lat * Math.PI / 180
    const deltaLat = (puntoCliente.lat - posicionRepartidor.lat) * Math.PI / 180
    const deltaLng = (puntoCliente.lng - posicionRepartidor.lng) * Math.PI / 180

    const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
              Math.cos(lat1) * Math.cos(lat2) *
              Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    const distancia = R * c

    setDistanciaRestante(distancia)

    // Calcular tiempo estimado (asumiendo velocidad promedio de 40 km/h)
    const velocidadPromedio = 40 // km/h
    const tiempoHoras = distancia / 1000 / velocidadPromedio
    const tiempoMinutos = Math.ceil(tiempoHoras * 60)
    setTiempoEstimado(tiempoMinutos)

    // Determinar estado
    if (distancia < 100) {
      setEstado('llegando')
    } else if (distancia < 500) {
      setEstado('cerca')
    } else if (simulando) {
      setEstado('en-camino')
    } else {
      setEstado('esperando')
    }
  }, [posicionRepartidor, puntoCliente, simulando])

  // Si no hay punto de cliente, mostrar mensaje con más información
  if (!puntoCliente) {
    return (
      <div className="seguimiento-cliente">
        <div className="seguimiento-error">
          <h2>Pedido no encontrado</h2>
          <p>El enlace de seguimiento no es válido o la simulación no ha comenzado.</p>
          {id && (
            <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#f3f4f6', borderRadius: '0.5rem' }}>
              <p style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                <strong>ID buscado:</strong> {id}
              </p>
              {puntosEntrega && puntosEntrega.length > 0 && (
                <p style={{ fontSize: '0.9rem', color: '#6b7280', marginTop: '0.5rem' }}>
                  <strong>Puntos disponibles:</strong> {puntosEntrega.length}
                </p>
              )}
              {!simulando && (
                <p style={{ fontSize: '0.9rem', color: '#f59e0b', marginTop: '0.5rem' }}>
                  ⚠️ La simulación aún no ha comenzado. Vuelve a la página principal e inicia la simulación.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="seguimiento-cliente">
      <header className="seguimiento-header">
        <h1>TuRutaTrack - Seguimiento de Pedido</h1>
        <p>Rastrea tu pedido en tiempo real</p>
      </header>

      <div className="seguimiento-contenido">
        {/* Panel de información */}
        <aside className="seguimiento-info">
          <div className="info-pedido">
            <h2>Información del Pedido</h2>
            <div className="info-item">
              <strong>Número de Pedido:</strong>
              <span>{datosCliente.numeroPedido}</span>
            </div>
            <div className="info-item">
              <strong>Cliente:</strong>
              <span>{datosCliente.nombre}</span>
            </div>
            <div className="info-item">
              <strong>Teléfono:</strong>
              <span>{datosCliente.telefono}</span>
            </div>
            <div className="info-item">
              <strong>Dirección:</strong>
              <span>{datosCliente.direccion}</span>
            </div>
          </div>

          <div className="info-estado">
            <h2>Estado del Repartidor</h2>
            <div className={`estado-badge estado-${estado}`}>
              {estado === 'esperando' && '⏳ Esperando inicio de ruta'}
              {estado === 'en-camino' && '🚚 En camino hacia tu ubicación'}
              {estado === 'cerca' && '📍 Cerca de tu ubicación'}
              {estado === 'llegando' && '🏠 Llegando a tu ubicación'}
            </div>

            {distanciaRestante > 0 && (
              <div className="info-distancia">
                <div className="distancia-item">
                  <strong>Distancia restante:</strong>
                  <span className="distancia-valor">
                    {distanciaRestante < 1000 
                      ? `${Math.round(distanciaRestante)} m`
                      : `${(distanciaRestante / 1000).toFixed(2)} km`
                    }
                  </span>
                </div>
                {tiempoEstimado > 0 && (
                  <div className="distancia-item">
                    <strong>Tiempo estimado:</strong>
                    <span className="distancia-valor">
                      {tiempoEstimado} {tiempoEstimado === 1 ? 'minuto' : 'minutos'}
                    </span>
                  </div>
                )}
              </div>
            )}

            {!simulando && (
              <div className="info-advertencia">
                <p>⚠️ La simulación aún no ha comenzado</p>
              </div>
            )}
          </div>
        </aside>

        {/* Mapa de seguimiento */}
        <main className="seguimiento-mapa">
          <MapaSeguimiento
            posicionRepartidor={posicionRepartidor}
            puntoCliente={puntoCliente}
            ruta={ruta}
            simulando={simulando}
          />
        </main>
      </div>
    </div>
  )
}

export default SeguimientoCliente

